import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Phone, Loader2 } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { ReadingProgress } from "@/components/ReadingProgress";
import { ArticleAreasWidget } from "@/components/ArticleAreasWidget";
import { AuthorByline } from "@/components/AuthorByline";
import { getArticle, getArticles } from "@/lib/api";
import { COMPANY, BOOKING_URL } from "@/data/site";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

const BODY_CLASS =
  "article-body max-w-none break-words " +
  // headings — scale down on mobile
  "[&_h2]:font-heading [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-bold [&_h2]:text-ink [&_h2]:mt-9 [&_h2]:sm:mt-10 [&_h2]:mb-3 [&_h2]:leading-snug " +
  "[&_h3]:font-heading [&_h3]:text-lg [&_h3]:sm:text-xl [&_h3]:font-bold [&_h3]:text-ink [&_h3]:mt-7 [&_h3]:sm:mt-8 [&_h3]:mb-2 [&_h3]:leading-snug " +
  // paragraphs — 16px mobile, 18px desktop, generous line-height
  "[&_p]:text-ink/75 [&_p]:text-[16px] [&_p]:sm:text-[17px] [&_p]:md:text-lg [&_p]:leading-[1.75] [&_p]:mb-5 " +
  // lists — same responsive scale as paragraphs
  "[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:text-ink/75 [&_ul]:text-[16px] [&_ul]:sm:text-[17px] [&_ul]:md:text-lg " +
  "[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-5 [&_ol]:text-ink/75 [&_ol]:text-[16px] [&_ol]:sm:text-[17px] [&_ol]:md:text-lg " +
  "[&_li]:leading-[1.7] [&_li]:mb-2 " +
  // links, emphasis
  "[&_a]:text-brand [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2 [&_a]:break-words " +
  "[&_strong]:font-bold [&_strong]:text-ink [&_b]:font-bold [&_b]:text-ink [&_em]:italic " +
  // any images or embeds coming from RSS render fluidly
  "[&_img]:w-full [&_img]:h-auto [&_img]:rounded-xl [&_img]:my-6 " +
  "[&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-xl [&_iframe]:my-6 " +
  // tables scroll instead of blowing out the width
  "[&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:my-6 " +
  "[&_blockquote]:border-l-4 [&_blockquote]:border-brand [&_blockquote]:pl-4 [&_blockquote]:sm:pl-6 [&_blockquote]:italic [&_blockquote]:text-ink/80 [&_blockquote]:my-6";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [more, setMore] = useState([]);
  const [all, setAll] = useState([]);
  const [state, setState] = useState("loading");
  const articleRef = useRef(null);

  useEffect(() => {
    setState("loading");
    getArticle(slug)
      .then((a) => { setArticle(a); setState("ok"); })
      .catch(() => setState("notfound"));
    getArticles().then((list) => {
      setAll(list || []);
      setMore((list || []).filter((a) => a.slug !== slug).slice(0, 3));
    }).catch(() => {});
  }, [slug]);

  const wordCount = useMemo(() => {
    if (!article?.body) return 0;
    const plain = article.body.replace(/<[^>]+>/g, " ").replace(/&[a-z]+;/g, " ");
    return plain.trim().split(/\s+/).filter(Boolean).length;
  }, [article]);

  // Pick 2 topically-related articles for inline recommender.
  // Prefer same category, fall back to any recent article if not enough.
  const related = useMemo(() => {
    if (!article || !all.length) return [];
    const sameCat = all.filter((a) => a.slug !== slug && a.category === article.category);
    const others = all.filter((a) => a.slug !== slug && a.category !== article.category);
    return [...sameCat, ...others].slice(0, 2);
  }, [article, all, slug]);

  // Split body at ~midway paragraph so recommendations sit inside the flow.
  const [topHtml, bottomHtml] = useMemo(() => {
    const body = article?.body || "";
    if (!body) return ["", ""];
    // find paragraph boundaries; place the split roughly halfway through paragraphs
    const boundaries = [];
    const re = /<\/p>/gi;
    let m;
    while ((m = re.exec(body)) !== null) boundaries.push(m.index + m[0].length);
    if (boundaries.length < 4) return [body, ""]; // too short to interrupt
    const cut = boundaries[Math.floor(boundaries.length / 2)];
    return [body.slice(0, cut), body.slice(cut)];
  }, [article]);

  if (state === "loading") {
    return <Layout><div className="py-40 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div></Layout>;
  }
  if (state === "notfound") {
    return (
      <Layout><div className="py-40 text-center">
        <p className="text-ink/60 mb-5">Article not found.</p>
        <Link to="/blog" className="bg-brand text-white px-6 py-3 rounded-full font-bold">Back to blog</Link>
      </div></Layout>
    );
  }

  const articleJsonLd = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: (typeof window !== "undefined" ? window.location.origin : "") + (article.image || ""),
    datePublished: article.date,
    dateModified: article.date,
    author: {
      "@type": "Organization",
      name: "The Clocktower Cars local team",
      url: (typeof window !== "undefined" ? window.location.origin : "") + "/about",
    },
    publisher: {
      "@type": "Organization",
      name: COMPANY.name,
      logo: {
        "@type": "ImageObject",
        url: (typeof window !== "undefined" ? window.location.origin : "") + "/images/clocktower-logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": (typeof window !== "undefined" ? window.location.origin : "") + `/blog/${article.slug}`,
    },
    articleSection: article.category,
  } : null;

  return (
    <Layout>
      <Seo title={article.title} description={article.excerpt} path={`/blog/${article.slug}`} image={article.image} type="article" jsonLd={articleJsonLd ? [articleJsonLd] : undefined} />
      <ReadingProgress targetRef={articleRef} wordCount={wordCount} />
      <article ref={articleRef} className="bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-6 sm:pb-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-ink/50 hover:text-brand transition-colors text-sm mb-6 sm:mb-8">
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4 sm:mb-5 text-xs sm:text-sm">
            <span className="bg-brand/10 text-brand font-semibold px-3 py-1 rounded-full">{article.category}</span>
            <span className="text-ink/40 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {article.read_time}</span>
            <span className="text-ink/40 hidden sm:inline">·</span>
            <span className="text-ink/40">{fmtDate(article.date)}</span>
          </div>
          <h1 data-testid="article-title" className="font-heading text-[28px] sm:text-4xl md:text-5xl font-black tracking-tight text-ink leading-tight sm:leading-[1.1] break-words">{article.title}</h1>
        </div>

        {article.image && (
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
            <motion.img initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} src={article.image} alt={article.title} className="w-full h-56 sm:h-80 md:h-[440px] object-cover rounded-2xl sm:rounded-3xl" />
          </div>
        )}

        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-10 sm:py-12">
          <AuthorByline date={article.date} readTime={article.read_time} />

          <div data-testid="article-body" className={`${BODY_CLASS} mt-8 sm:mt-10`} dangerouslySetInnerHTML={{ __html: bottomHtml ? topHtml : (article.body || "") }} />

          {bottomHtml && related.length > 0 && (
            <aside data-testid="article-inline-related" className="my-10 sm:my-12 not-prose">
              <div className="flex items-baseline justify-between mb-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand">Keep reading</p>
                <Link to="/blog" className="text-xs text-ink/50 hover:text-brand transition-colors font-semibold">All articles →</Link>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    data-testid={`article-related-${r.slug}`}
                    className="group flex gap-4 bg-surface hover:bg-white border border-[#EAEBEA] hover:border-brand/40 hover:shadow-[0_12px_28px_-16px_rgba(0,0,0,0.15)] rounded-2xl p-3 transition-all duration-300"
                  >
                    <div className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 overflow-hidden rounded-xl">
                      <img src={r.image} alt={r.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[600ms]" />
                    </div>
                    <div className="min-w-0 flex flex-col justify-center">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-brand">{r.category}</span>
                      <h4 className="font-heading text-sm sm:text-base font-bold text-ink leading-snug mt-1 line-clamp-3 group-hover:text-brand transition-colors">{r.title}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </aside>
          )}

          {bottomHtml && (
            <div data-testid="article-body-bottom" className={BODY_CLASS} dangerouslySetInnerHTML={{ __html: bottomHtml }} />
          )}

          <ArticleAreasWidget articleBody={article.body || ""} />

          <div className="mt-10 sm:mt-12 bg-ink rounded-2xl p-6 sm:p-8 text-center">
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-white">Need a ride?</h3>
            <p className="text-white/60 text-sm sm:text-base mt-2 mb-6">Book a fixed-price transfer or call our 24/7 team.</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-center gap-3">
              <a href={BOOKING_URL} className="bg-brand hover:bg-brand-light text-white px-7 py-3.5 rounded-full font-bold transition-colors">Book online</a>
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-white text-white px-7 py-3.5 rounded-full font-bold transition-colors"><Phone className="h-4 w-4" /> {COMPANY.phone}</a>
            </div>
          </div>
        </div>
      </article>

      {more.length > 0 && (
        <section className="bg-surface py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-ink mb-6 sm:mb-8">More articles</h3>
            <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 md:grid-cols-3">
              {more.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`} className="group block bg-white border border-[#EAEBEA] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <div className="h-40 sm:h-44 overflow-hidden"><img src={a.image} alt={a.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-5 sm:p-6">
                    <span className="text-xs bg-surface text-ink/70 font-semibold px-2.5 py-1 rounded-full">{a.category}</span>
                    <h4 className="font-heading text-base sm:text-lg font-bold text-ink mt-3 leading-snug group-hover:text-brand transition-colors line-clamp-3">{a.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
